import { useEffect } from 'react';

interface ShortcutItem {
  keys: string[];
  description: string;
}

interface ShortcutSection {
  title: string;
  icon: string;
  items: ShortcutItem[];
}

const SECTIONS: ShortcutSection[] = [
  {
    title: 'General',
    icon: 'ri-command-line',
    items: [
      { keys: ['?'], description: 'Toggle this shortcuts panel' },
      { keys: ['Esc'], description: 'Close any open panel or modal' },
    ],
  },
  {
    title: 'Generator',
    icon: 'ri-video-line',
    items: [
      { keys: ['P'], description: 'Switch to Prompt tab' },
      { keys: ['S'], description: 'Switch to Script tab' },
      { keys: ['E'], description: 'AI-enhance current prompt' },
      { keys: ['G'], description: 'Focus / trigger Generate' },
      { keys: ['Q'], description: 'Add current config to Queue' },
    ],
  },
  {
    title: 'Panels & Navigation',
    icon: 'ri-layout-line',
    items: [
      { keys: ['D'], description: 'Open / close Drafts panel' },
      { keys: ['H'], description: 'Open / close History panel' },
      { keys: ['Shift', 'Q'], description: 'Open / close Generation Queue' },
    ],
  },
  {
    title: 'Video Player',
    icon: 'ri-play-circle-line',
    items: [
      { keys: ['Space'], description: 'Play / Pause' },
      { keys: ['K'], description: 'Play / Pause (alternative)' },
      { keys: ['←'], description: 'Seek back 5 seconds' },
      { keys: ['→'], description: 'Seek forward 5 seconds' },
      { keys: ['M'], description: 'Toggle mute' },
      { keys: ['F'], description: 'Toggle fullscreen' },
    ],
  },
  {
    title: 'Drafts Panel',
    icon: 'ri-draft-line',
    items: [
      { keys: ['Tab'], description: 'Switch between Configs / History' },
      { keys: ['Enter'], description: 'Confirm rename / restore draft' },
    ],
  },
];

interface KeyboardShortcutsOverlayProps {
  isOpen: boolean;
  onClose: () => void;
  darkMode: boolean;
}

function KeyBadge({ label, darkMode }: { label: string; darkMode: boolean }) {
  return (
    <kbd className={`inline-flex items-center justify-center px-2 py-1 rounded-md text-[11px] font-bold font-mono leading-none border ${
      darkMode
        ? 'bg-gray-700 border-gray-600 text-gray-200'
        : 'bg-white border-gray-300 text-gray-700'
    }`} style={{ minWidth: '1.8rem', boxShadow: darkMode ? '0 1px 0 #374151' : '0 1px 0 #d1d5db' }}>
      {label}
    </kbd>
  );
}

export default function KeyboardShortcutsOverlay({ isOpen, onClose, darkMode }: KeyboardShortcutsOverlayProps) {
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const overlay = darkMode ? 'bg-gray-900/95 border-gray-700' : 'bg-white border-gray-200';
  const sectionBg = darkMode ? 'bg-gray-800/60' : 'bg-gray-50';
  const muted = darkMode ? 'text-gray-400' : 'text-gray-500';
  const divider = darkMode ? 'border-gray-700' : 'border-gray-100';

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-md" onClick={onClose} />

      <div className={`relative w-full max-w-2xl max-h-[85vh] rounded-2xl border overflow-hidden flex flex-col ${overlay}`}
           style={{ animation: 'fadeInUp 0.2s ease-out' }}>
        {/* Header */}
        <div className={`px-6 py-4 border-b flex items-center justify-between flex-shrink-0 ${divider}`}>
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-red-500 to-orange-600 flex items-center justify-center">
              <i className="ri-keyboard-line text-white text-base"></i>
            </div>
            <div>
              <h3 className={`font-bold text-base ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                Keyboard Shortcuts
              </h3>
              <p className={`text-[11px] ${muted}`}>Navigate the whole page without a mouse</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1.5">
              <KeyBadge label="?" darkMode={darkMode} />
              <span className={`text-xs ${muted}`}>to toggle</span>
            </div>
            <button
              onClick={onClose}
              className={`w-8 h-8 flex items-center justify-center rounded-lg transition-colors cursor-pointer ${
                darkMode ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-500'
              }`}
            >
              <i className="ri-close-line text-lg"></i>
            </button>
          </div>
        </div>

        {/* Sections */}
        <div className="flex-1 overflow-y-auto p-5">
          <div className="grid sm:grid-cols-2 gap-4">
            {SECTIONS.map(section => (
              <div key={section.title} className={`rounded-xl p-4 ${sectionBg}`}>
                <div className="flex items-center gap-2 mb-3">
                  <i className={`${section.icon} text-red-500 text-sm`}></i>
                  <h4 className={`text-xs font-bold uppercase tracking-wider ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                    {section.title}
                  </h4>
                </div>
                <div className="space-y-2.5">
                  {section.items.map((item, i) => (
                    <div key={i} className="flex items-center justify-between gap-3">
                      <span className={`text-xs flex-1 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                        {item.description}
                      </span>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        {item.keys.map((key, ki) => (
                          <span key={ki} className="flex items-center gap-1">
                            {ki > 0 && <span className={`text-[10px] ${muted}`}>+</span>}
                            <KeyBadge label={key} darkMode={darkMode} />
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Pro tip */}
          <div className={`mt-4 rounded-xl p-4 border ${darkMode ? 'border-orange-800/50 bg-orange-900/10' : 'border-orange-200 bg-orange-50'}`}>
            <div className="flex items-start gap-3">
              <i className={`ri-lightbulb-line text-sm mt-0.5 flex-shrink-0 ${darkMode ? 'text-orange-400' : 'text-orange-600'}`}></i>
              <div>
                <p className={`text-xs font-bold mb-1 ${darkMode ? 'text-orange-300' : 'text-orange-700'}`}>Pro Tip</p>
                <p className={`text-xs leading-relaxed ${darkMode ? 'text-orange-400/80' : 'text-orange-600/80'}`}>
                  Shortcuts are disabled when you&#39;re typing inside an input or textarea — so you can always type freely without accidentally triggering actions.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className={`px-5 py-3 border-t text-center flex-shrink-0 ${divider}`}>
          <p className={`text-[11px] ${muted}`}>
            Press <kbd className={`px-1.5 py-0.5 rounded text-[10px] font-mono font-bold mx-0.5 ${
              darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'
            }`}>Esc</kbd> or click outside to close
          </p>
        </div>
      </div>

      <style>{`@keyframes fadeInUp { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: translateY(0); } }`}</style>
    </div>
  );
}
